/**
 * Blog post editor — same shape as News editor, except author is bound to
 * the creating user and shown read-only here.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, ImagePlus, Save, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEPT_OPTIONS } from "@/lib/departments";
import { TipTapEditor } from "@/components/editor/TipTapEditor";
import { MediaPicker } from "@/components/editor/MediaPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { useAutosave, formatSavedAt } from "@/hooks/useAutosave";
import { slugify } from "@/hooks/useSlug";

export const Route = createFileRoute("/_app/content/blog/$id")({ component: BlogEditor });

interface BlogDetail {
  id: number; slug: string; title: string;
  author_id: number | null; excerpt: string | null; body_md: string | null;
  cover_r2_key: string | null; tags_csv: string | null; dept: string | null;
  status: "draft" | "published" | "archived";
  published_at: number | null; updated_at: number;
}

const schema = z.object({
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  excerpt: z.string().max(500).nullable(),
  body_md: z.string().max(500_000).nullable(),
  cover_r2_key: z.string().nullable(),
  tags_csv: z.string().max(300).nullable(),
  dept: z.string().max(60).nullable(),
});
type FormValues = z.infer<typeof schema>;

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const r2Url = (k: string) => `${apiBase}/admin/media/file/${k}`;

function BlogEditor() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ["blog", "detail", id],
    queryFn: () => api.get<BlogDetail>(`/admin/blog/${id}`),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: row ? {
      title: row.title, slug: row.slug, excerpt: row.excerpt, body_md: row.body_md,
      cover_r2_key: row.cover_r2_key, tags_csv: row.tags_csv, dept: row.dept,
    } : undefined,
  });
  const { register, handleSubmit, watch, setValue, control, formState: { errors, isDirty } } = form;

  // List only — refetching the detail would feed the server row back through
  // useForm's `values` and reset the form mid-edit. See events editor.
  const update = useMutation({ mutationFn: (v: Partial<FormValues>) => api.patch(`/admin/blog/${id}`, v), onSuccess: () => qc.invalidateQueries({ queryKey: ["blog", "list"] }) });
  const publish = useMutation({ mutationFn: () => api.post(`/admin/blog/${id}/publish`), onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["blog"] }); } });
  const unpublish = useMutation({ mutationFn: () => api.post(`/admin/blog/${id}/unpublish`), onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["blog"] }); } });

  const formValues = watch();
  const { status: saveStatus, savedAt } = useAutosave({
    value: formValues, enabled: !!row && form.formState.isValid && isDirty, delayMs: 1500,
    onSave: (v) => update.mutateAsync(v),
  });

  const slugTouched = useRef(false);
  const lastTitle = useRef("");
  useEffect(() => {
    const t = formValues.title;
    if (!t || slugTouched.current) return;
    if (t === lastTitle.current) return;
    lastTitle.current = t;
    setValue("slug", slugify(t));
  }, [formValues.title, setValue]);

  if (isLoading || !row) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.title || "Untitled"}
        back={{ href: "/content/blog", label: "Blog" }}
        actions={
          <>
            <span className="text-xs text-muted-foreground">
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && `Saved ${formatSavedAt(savedAt)}`}
              {saveStatus === "dirty" && "Unsaved changes"}
              {saveStatus === "error" && <span className="text-destructive">Save failed</span>}
            </span>
            <Button variant="outline" size="sm" disabled={!can("blog", "update")} onClick={handleSubmit((v) => update.mutateAsync(v).then(() => toast.success("Saved")))}>
              <Save className="h-4 w-4" /> Save
            </Button>
            {row.status === "published" ? (
              <Button variant="secondary" size="sm" disabled={!can("blog", "publish")} onClick={() => unpublish.mutate()}><EyeOff className="h-4 w-4" /> Unpublish</Button>
            ) : (
              <Button size="sm" disabled={!can("blog", "publish")} onClick={() => publish.mutate()}><Eye className="h-4 w-4" /> Publish</Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Input className="h-12 border-0 px-0 text-3xl font-light tracking-tight focus-visible:ring-0" placeholder="Post title" {...register("title")} />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>/blog/</span>
                <Input className="h-7 max-w-[300px] border-0 bg-secondary px-2 font-mono text-xs"
                  {...register("slug", { onChange: () => { slugTouched.current = true; } })} />
                {errors.slug && <span className="text-destructive text-xs">{errors.slug.message}</span>}
              </div>
              <Textarea rows={2} placeholder="Lede / excerpt…" {...register("excerpt")} />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="px-0 pt-0 pb-0">
              <Controller
                control={control} name="body_md"
                render={({ field }) => (<TipTapEditor value={field.value ?? ""} onChange={field.onChange} surface="article" className="border-0 shadow-none" />)}
              />
            </CardContent>
          </Card>
        </form>

        <aside className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Current</span><StatusBadge status={row.status} /></div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Author</span><span>{row.author_id ? `#${row.author_id}` : "—"}</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Published</span><span>{row.published_at ? format(row.published_at * 1000, "MMM d, yyyy") : "—"}</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Updated</span><span>{format(row.updated_at * 1000, "MMM d, yyyy")}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cover image</CardTitle></CardHeader>
            <CardContent>
              {watch("cover_r2_key") ? (
                <div className="space-y-2">
                  <img src={r2Url(watch("cover_r2_key")!)} alt="" className="aspect-video w-full rounded-md border object-cover" />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setCoverPickerOpen(true)}><ImagePlus className="h-4 w-4" /> Replace</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setValue("cover_r2_key", null, { shouldDirty: true })}><XIcon className="h-4 w-4" /> Remove</Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setCoverPickerOpen(true)}><ImagePlus className="h-4 w-4" /> Choose image</Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Department</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Controller
                control={control}
                name="dept"
                render={({ field }) => (
                  <Select value={field.value ?? "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}>
                    <SelectTrigger><SelectValue placeholder="— None —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {DEPT_OPTIONS.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-xs text-muted-foreground">If set, this post also appears on the department's page under its blog strip.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tags</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="tags_csv" className="sr-only">Tags</Label>
              <Input id="tags_csv" placeholder="hackathon, ai, krct-life" {...register("tags_csv")} />
              <p className="text-xs text-muted-foreground">Comma-separated.</p>
              <Separator />
              <Link to="/content/blog" as never className="block text-xs text-muted-foreground hover:text-primary">← All posts</Link>
            </CardContent>
          </Card>
        </aside>
      </div>

      <MediaPicker
        open={coverPickerOpen}
        onOpenChange={setCoverPickerOpen}
        onPick={(url) => {
          const key = url.split("/admin/media/file/")[1] ?? "";
          setValue("cover_r2_key", key, { shouldDirty: true });
          setCoverPickerOpen(false);
        }}
      />
    </div>
  );
}
